"use client";

import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { useTranslation } from 'react-i18next';
import { useMultipleIndividualFormFields } from "@/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BubbleMapAppearanceEditor from "@/components/dashboard/widgets/bubble-map/bubble-map-appearance-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormAutocomplete } from "@/components/widget-builder/form-autocomplete";

interface BubbleMapMetric {
  formId: string;
  countryFieldId: string;
  cityFieldId: string;
  valueFieldId: string;
}

interface BubbleMapAppearance {
  showLegend?: boolean;
  legendLabel?: string;
  legendLocation?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";
  fillColor?: string;
  borderColor?: string;
  showCityName?: boolean;
  cityNameColor?: string;
  showValueTooltip?: boolean;
  countryFillColor?: string;
  countryHoverColor?: string;
}

interface BubbleMapConfigValue {
  metric: BubbleMapMetric;
  filters: any[];
  appearance: BubbleMapAppearance;
}

interface BubbleMapConfigPanelProps {
  value: BubbleMapConfigValue;
  onChange: (val: BubbleMapConfigValue) => void;
  forms: any[];
}

export default function BubbleMapConfigPanel({ value, onChange, forms }: BubbleMapConfigPanelProps) {
  const { t } = useTranslation();
  const metricFormId = value.metric?.formId || "";
  const { formFieldsMap } = useMultipleIndividualFormFields(metricFormId ? [metricFormId] : []);

  const updateMetric = (updates: Partial<BubbleMapMetric>) => {
    onChange({
      ...value,
      metric: { ...value.metric, ...updates } as BubbleMapMetric,
    });
  };

  const fields = formFieldsMap[metricFormId] || [];
  const countryFields = (forms.find((f: any) => f.id === metricFormId)?.countryFields) || [];

  // Filter fields for city (text type) and value (number type)
  const cityFields = fields.filter((f: any) =>
    f.type === "text" || f.type === "Text" || f.type === "ShortText" || f.type === "LongText"
  );
  const valueFields = fields.filter((f: any) =>
    f.type === "number" || f.type === "Number" || f.type === "Decimal"
  );

  // Get value field label for default legend label
  const valueField = valueFields.find((f: any) => f.id === value.metric?.valueFieldId);
  const defaultLegendLabel = valueField?.label || "Value";

  const appearance = value.appearance || {
    showLegend: false,
    legendLabel: defaultLegendLabel,
    legendLocation: "bottom-center",
    fillColor: "#3b82f6",
    borderColor: "#ffffff",
    showCityName: true,
    cityNameColor: "#ffffff",
    showValueTooltip: true,
    countryFillColor: "#e5e7eb",
    countryHoverColor: "#d1d5db",
    countryBorderColor: "#d1d5db",
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('processManagement.metrics')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>{t('processManagement.form')}</Label>
              <FormAutocomplete
                value={value.metric?.formId || ""}
                onValueChange={(formId) => updateMetric({ formId: formId || "", countryFieldId: "", cityFieldId: "", valueFieldId: "" })}
                forms={forms.map((f: any) => ({
                  id: f.id,
                  title: f.title || f.name || "",
                  name: f.name || f.title || "",
                  description: f.description,
                }))}
                placeholder={t('processManagement.selectFormPlaceholder')}
              />
            </div>
            <div>
              <Label>{t('processManagement.countryField')}</Label>
              <Select
                value={value.metric?.countryFieldId || ""}
                onValueChange={(countryFieldId) => updateMetric({ countryFieldId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('processManagement.selectCountryField')} />
                </SelectTrigger>
                <SelectContent>
                  {countryFields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City Field</Label>
              <Select
                value={value.metric?.cityFieldId || ""}
                onValueChange={(cityFieldId) => updateMetric({ cityFieldId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select city field" />
                </SelectTrigger>
                <SelectContent>
                  {cityFields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('processManagement.valueField')}</Label>
              <Select
                value={value.metric?.valueFieldId || ""}
                onValueChange={(valueFieldId) => updateMetric({ valueFieldId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('processManagement.selectValueField')} />
                </SelectTrigger>
                <SelectContent>
                  {valueFields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('processManagement.appearance')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <BubbleMapAppearanceEditor
            appearance={appearance}
            defaultLegendLabel={defaultLegendLabel}
            onChange={(app) => onChange({ ...value, appearance: app })}
          />
        </CardContent>
      </Card>
    </div>
  );
}

